import { Link } from 'react-router-dom';
import { Pencil, ExternalLink, Archive, ArchiveRestore, Link2, Video, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Package as PartnerPackage, PackageVariant } from '../../../../shared/types/partnerships';
import { effectiveVariants, isPackageExpired } from '../../../../shared/partnerships-packages';
import { PACKAGE_SHEET_ENUM_LABELS } from '../../../../shared/partnerships-package-sheet';
import { EnumBadge } from '@/components/ui/enum-badge';
import { fmtRelative } from '@/lib/relative-time';

const TIER_BADGE: Record<string, string> = {
  // Google Brand Deals (Agency Sold)
  BRONZE:    'text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-700',
  SILVER:    'text-gray-500 border-gray-300 bg-gray-50 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-600',
  GOLD:      'text-yellow-600 border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:text-yellow-400 dark:border-yellow-600',
  PLATINUM:  'text-purple-600 border-purple-300 bg-purple-50 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-600',
  // Google Brand Deals (GCS)
  SPOTLIGHT: 'text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-600',
  VELOCITY:  'text-teal-600 border-teal-300 bg-teal-50 dark:bg-teal-950/30 dark:text-teal-400 dark:border-teal-600',
  CUSTOM:    'text-slate-600 border-slate-300 bg-slate-50 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-600',
  PREMIUM:   'text-rose-600 border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-600',
  // TikTok
  ESSENTIAL: 'text-emerald-700 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-700',
  STANDARD:  'text-indigo-600 border-indigo-300 bg-indigo-50 dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-600',
  LAUNCH:    'text-orange-600 border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-600',
  CREATOR:   'text-cyan-600 border-cyan-300 bg-cyan-50 dark:bg-cyan-950/30 dark:text-cyan-400 dark:border-cyan-600',
  FUNNEL:    'text-violet-600 border-violet-300 bg-violet-50 dark:bg-violet-950/30 dark:text-violet-400 dark:border-violet-600',
  // Meta
  CREADORES: 'text-fuchsia-600 border-fuchsia-300 bg-fuchsia-50 dark:bg-fuchsia-950/30 dark:text-fuchsia-400 dark:border-fuchsia-600',
  ADVOCATES: 'text-lime-700 border-lime-300 bg-lime-50 dark:bg-lime-950/30 dark:text-lime-400 dark:border-lime-700',
};

const DEFAULT_BADGE = 'text-muted-foreground border-border bg-muted/40';

/** Color del badge de un tier (la lista de paquetes y el detalle usan el mismo). */
// eslint-disable-next-line react-refresh/only-export-components -- helper de UI junto a su componente
export function tierBadgeClass(tier?: string | null): string {
  return (tier && TIER_BADGE[tier.toUpperCase()]) ?? DEFAULT_BADGE;
}

/** Nombre de la variante tal como se muestra: el nombre, o el tier si no tiene. */
// eslint-disable-next-line react-refresh/only-export-components -- helper de UI junto a su componente
export function variantLabel(v: PackageVariant, fallback: string): string {
  return v.name || v.tier || fallback;
}

/** "6 videos · 2+ creadores · USD 3.500": lo que cambia entre variantes, en una línea. */
function variantFacts(v: PackageVariant, t: (k: string, o?: Record<string, unknown>) => string): string[] {
  const facts: string[] = [];
  if (v.videos) facts.push(`${v.videos} ${t('partnerships.packagecard.videos')}`);
  if (v.minCreators) facts.push(`${v.minCreators}+ ${t('partnerships.packagecard.creators')}`);
  if (v.pricing) facts.push(`${v.currency ? `${v.currency} ` : ''}${v.pricing}`);
  else if (v.productionFee) facts.push(v.productionFee);
  return facts;
}

const MAX_VARIANT_ROWS = 4;

interface PackageCardProps {
  pkg: PartnerPackage;
  partnerSlug: string;
  canEdit?: boolean;
  onEdit?: (pkg: PartnerPackage) => void;
  /** Archivar (pide confirmación afuera). */
  onArchive?: (pkg: PartnerPackage) => void;
  /** Vista "Archivados": volver a Publicado. */
  onRestore?: (pkg: PartnerPackage) => void;
  restoring?: boolean;
}

/**
 * UNA tarjeta por pack con sus variantes adentro (pedido de Partnerships 13/10/2026;
 * antes cada tier era una tarjeta y "Package A / B / C - X" se leía como tres packs).
 */
export function PackageCard({ pkg, partnerSlug, canEdit, onEdit, onArchive, onRestore, restoring }: PackageCardProps) {
  const { t, i18n } = useTranslation();
  const variants = effectiveVariants(pkg);
  const shown = variants.slice(0, MAX_VARIANT_ROWS);
  const more = variants.length - shown.length;
  const summary = pkg.context || pkg.description;
  const e = pkg.eligibility;
  const eligibilityChips = [
    ...(e?.segments ?? []),
    ...(e?.accountSizes ?? []),
    ...(e?.clientType ? [t(`partnerships.package_sheet.enums.${e.clientType}`, { defaultValue: PACKAGE_SHEET_ENUM_LABELS[e.clientType] })] : []),
  ].slice(0, 4);
  const linksCount = (pkg.links?.length ?? 0) + (pkg.driveUrl ? 1 : 0);
  const expired = isPackageExpired(pkg);

  return (
    <div className="group border border-border rounded-lg overflow-hidden flex flex-col bg-card hover:shadow-xs transition-shadow">
      {/* Header */}
      <div className="px-4 py-3.5 border-b border-border/60 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold leading-snug">{pkg.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {pkg.salesModel && (
              <EnumBadge value={pkg.salesModel} tone="info" className="text-[10px] px-1.5 py-0" label={t(`partnerships.package_sheet.enums.${pkg.salesModel}`, { defaultValue: PACKAGE_SHEET_ENUM_LABELS[pkg.salesModel] })} />
            )}
            {pkg.status === 'DRAFT' && (
              <span className="rounded border border-dashed border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground" title={t('partnerships.packagecard.draft_hint')}>
                {t('partnerships.packagecard.draft')}
              </span>
            )}
            {expired && <EnumBadge value="expired" tone="danger" className="text-[10px] px-1.5 py-0" label={t('partnerships.package_sheet.expired')} />}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5 -mt-0.5">
          {canEdit && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(pkg)}
              aria-label={t('partnerships.packagecard.edit')}
              title={t('partnerships.packagecard.edit')}
              className="sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
            >
              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
          {canEdit && onArchive && (
            <button
              type="button"
              onClick={() => onArchive(pkg)}
              aria-label={t('partnerships.packagecard.archive')}
              title={t('partnerships.packagecard.archive')}
              className="sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
            >
              <Archive className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
          {canEdit && onRestore && (
            <button
              type="button"
              onClick={() => onRestore(pkg)}
              disabled={restoring}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-primary hover:bg-muted disabled:opacity-50"
            >
              <ArchiveRestore className="h-3.5 w-3.5" /> {t('partnerships.packagecard.restore')}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 flex-1 space-y-3">
        {summary && <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{summary}</p>}

        {variants.length > 0 ? (
          <div className="divide-y divide-border/40">
            {shown.map((v, i) => {
              const facts = variantFacts(v, t);
              return (
                <div key={v.variantId || i} className="flex items-center justify-between gap-2 py-1.5">
                  <span className={`inline-block max-w-[55%] truncate text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${tierBadgeClass(v.tier)}`}>
                    {variantLabel(v, t('partnerships.packagecard.single_variant'))}
                  </span>
                  <span className="text-xs text-right text-foreground/80 truncate">{facts.join(' · ') || '—'}</span>
                </div>
              );
            })}
            {more > 0 && (
              <p className="pt-1.5 text-[11px] text-muted-foreground">{t('partnerships.packagecard.variants_more', { count: more })}</p>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><Video className="h-3 w-3" /> —</span>
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> —</span>
          </div>
        )}

        {eligibilityChips.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {eligibilityChips.map((c) => (
              <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{c}</span>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-border/40 flex items-center justify-between gap-2">
        <Link
          to={`/partnerships/${partnerSlug}/packages/${pkg.packageId}`}
          className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
        >
          {t('partnerships.common.view_detail')}
        </Link>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {pkg.driveUrl ? (
            <a href={pkg.driveUrl} target="_blank" rel="noopener noreferrer" className="hover:text-foreground flex items-center gap-1 transition-colors">
              <ExternalLink className="w-3 h-3" /> One-pager{linksCount > 1 ? ` +${linksCount - 1}` : ''}
            </a>
          ) : linksCount > 0 ? (
            <span className="flex items-center gap-1"><Link2 className="w-3 h-3" /> {t('partnerships.packagecard.links_count', { count: linksCount })}</span>
          ) : null}
          <span title={pkg.updatedBy || undefined}>{t('partnerships.package_sheet.updated', { when: fmtRelative(pkg.updatedAt, i18n.language) })}</span>
        </div>
      </div>
    </div>
  );
}
